import type { PayorEnrollmentStatus } from '../../services/api/types.gen';

/**
 * What the intake tab and the patient chart each know about one coverage.
 *
 * The two surfaces store these facts in different shapes, so each maps its own
 * fields onto this rather than the rule learning about both.
 */
export interface EligibilityBlockInput {
  /** The payer can take a 270 at all. */
  supported: boolean;
  /** Enrollment for the asking clinic, as the server resolved it. */
  enrollmentStatus?: PayorEnrollmentStatus | null;
  hasMemberId: boolean;
}

export const ELIGIBILITY_UNSUPPORTED_MESSAGE =
  'Eligibility checks are not available for this payer.';
export const ELIGIBILITY_ENROLLMENT_PENDING_MESSAGE =
  'Payer enrollment is still in progress.';
export const ELIGIBILITY_ENROLLMENT_FAILED_MESSAGE =
  'Payer enrollment was not completed.';
export const ELIGIBILITY_NO_MEMBER_ID_MESSAGE =
  'Add a member ID before running a check.';

/**
 * Why an eligibility check cannot run, or undefined when it can.
 *
 * Both the intake Eligibility tab and the chart Insurance tab call this, so the two
 * cannot drift into saying different things about the same coverage. The wording
 * matches what the server refuses with, so a disabled button and a 400 agree.
 *
 * The order is the order the user should act in. A payer that cannot do eligibility
 * has no meaningful enrollment state, and neither is worth mentioning if the coverage
 * has no member ID to look the patient up by.
 *
 * An unknown enrollment status never blocks. Enrollment is refreshed by hand from
 * Admin > Insurance, so an agency that has never refreshed it has nothing stored, and
 * blocking on that would disable the button with nothing the user could do about it.
 */
export function eligibilityBlockReason({
  supported,
  enrollmentStatus,
  hasMemberId,
}: EligibilityBlockInput): string | undefined {
  if (!supported) return ELIGIBILITY_UNSUPPORTED_MESSAGE;
  if (enrollmentStatus === 'in_progress')
    return ELIGIBILITY_ENROLLMENT_PENDING_MESSAGE;
  if (enrollmentStatus === 'failed')
    return ELIGIBILITY_ENROLLMENT_FAILED_MESSAGE;
  if (!hasMemberId) return ELIGIBILITY_NO_MEMBER_ID_MESSAGE;
  return undefined;
}
